import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { uploadDocumentoPaziente } from '../services/documentiPaziente';

/** File in parallelo (ognuno già a chunk su S3): basso per non saturare la rete utente. */
const MAX_PARALLEL_UPLOADS = 1;

const UploadContext = createContext(null);

const createTaskId = (idAppuntamento, file) =>
  `${idAppuntamento}-${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;

export const PazienteDocumentUploadProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const queueRef = useRef([]);
  const activeCountRef = useRef(0);

  const updateTask = useCallback((id, patch) => {
    setTasks((prev) => prev.map((task) => (task.id === id ? { ...task, ...patch } : task)));
  }, []);

  const runNext = useCallback(() => {
    while (activeCountRef.current < MAX_PARALLEL_UPLOADS && queueRef.current.length > 0) {
      const task = queueRef.current.shift();
      activeCountRef.current += 1;
      updateTask(task.id, { status: 'uploading', progress: 0 });

      uploadDocumentoPaziente(
        task.idAppuntamento,
        task.file,
        (progress) => updateTask(task.id, { progress }),
        { descrizione: task.descrizione }
      )
        .then((documento) => {
          updateTask(task.id, {
            status: 'completed',
            progress: 100,
            documento,
            completedAt: new Date().toISOString()
          });
        })
        .catch((error) => {
          console.error('Errore upload documento paziente:', error);
          updateTask(task.id, {
            status: 'error',
            error: error.response?.data?.message || 'Upload non riuscito'
          });
        })
        .finally(() => {
          activeCountRef.current = Math.max(0, activeCountRef.current - 1);
          runNext();
        });
    }
  }, [updateTask]);

  const enqueueUploads = useCallback((idAppuntamento, files, options = {}) => {
    const fileList = Array.from(files || []);
    if (!idAppuntamento || fileList.length === 0) return [];

    const newTasks = fileList.map((file) => ({
      id: createTaskId(idAppuntamento, file),
      idAppuntamento: Number(idAppuntamento),
      file,
      filename: file.name,
      size: file.size,
      status: 'queued',
      progress: 0,
      descrizione: options.descrizione || 'Documentazione sanitaria paziente',
      createdAt: new Date().toISOString()
    }));

    queueRef.current.push(...newTasks);
    setTasks((prev) => [...newTasks, ...prev].slice(0, 100));
    setTimeout(runNext, 0);
    return newTasks;
  }, [runNext]);

  const dismissTask = useCallback((id) => {
    setTasks((prev) => prev.filter((task) => task.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setTasks((prev) => prev.filter((task) => task.status !== 'completed'));
  }, []);

  const value = useMemo(() => ({
    tasks,
    enqueueUploads,
    dismissTask,
    clearCompleted
  }), [tasks, enqueueUploads, dismissTask, clearCompleted]);

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};

export const usePazienteDocumentUploads = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('usePazienteDocumentUploads deve essere usato dentro PazienteDocumentUploadProvider');
  }
  return context;
};
